import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "../Header";
import Footer from "../Footer";
import useSeo from "../../hooks/useSeo";
import { formatPrice, shopGet } from "../../api/shopApi";

const STATUS_FILTERS = [
  { value: "", label: "全部" },
  { value: "pending_confirmation", label: "待确认" },
  { value: "confirmed", label: "已确认" },
  { value: "preparing_shipment", label: "备货中" },
  { value: "shipped", label: "已发货" },
  { value: "completed", label: "已完成" },
  { value: "cancelled", label: "已取消" },
];

export default function ShopOrdersPage() {
  const navigate = useNavigate();
  const userId = Number(localStorage.getItem("user_id"));
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, page_size: 10 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useSeo({ title: "我的订单 - 生活好物商城", path: "/shop/orders", noindex: true });

  useEffect(() => {
    if (!userId) {
      navigate(`/login?redirect=${encodeURIComponent("/shop/orders")}`, { replace: true });
      return;
    }
    setLoading(true);
    const q = new URLSearchParams({
      user_id: String(userId),
      page: String(page),
      page_size: "10",
    });
    if (status) q.set("status", status);
    shopGet(`/orders?${q}`)
      .then(setData)
      .catch((e) => setError(e.message || "加载失败"))
      .finally(() => setLoading(false));
  }, [userId, status, page, navigate]);

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / (data.page_size || 10)));

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-amber-50 flex flex-col">
      <Header />
      <main className="flex-1 px-4 py-8">
        <div className="max-w-3xl mx-auto space-y-5">
          <div className="flex items-center justify-between gap-3">
            <h1 className="text-2xl font-bold text-gray-800">我的订单</h1>
            <Link to="/shop" className="text-sm text-pink-600 hover:underline">
              去商城逛逛
            </Link>
          </div>
          <div className="flex flex-wrap gap-2">
            {STATUS_FILTERS.map((s) => (
              <button
                key={s.value || "all"}
                type="button"
                onClick={() => {
                  setStatus(s.value);
                  setPage(1);
                }}
                className={`px-3 py-1.5 rounded-full text-sm border ${
                  status === s.value
                    ? "bg-pink-500 text-white border-pink-500"
                    : "bg-white text-gray-600 border-gray-200"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
          {loading ? (
            <p className="text-center text-gray-400 py-12">加载中…</p>
          ) : error ? (
            <p className="text-center text-rose-500 py-12">{error}</p>
          ) : !(data.items || []).length ? (
            <p className="text-center text-gray-400 py-12">暂无订单</p>
          ) : (
            <div className="space-y-3">
              {(data.items || []).map((o) => (
                <Link
                  key={o.order_no}
                  to={`/shop/orders/${o.order_no}`}
                  className="block bg-white rounded-2xl shadow-sm p-4 hover:shadow-md transition"
                >
                  <div className="flex justify-between gap-3 text-sm">
                    <span className="text-gray-500">订单号 {o.order_no}</span>
                    <span className="text-pink-600 font-medium">{o.status_label}</span>
                  </div>
                  <p className="mt-2 text-gray-800 font-medium line-clamp-1">
                    {(o.items || []).map((i) => i.product_name).join("、") || "商品"}
                  </p>
                  <div className="mt-2 flex justify-between text-sm text-gray-500">
                    <span>{o.created_at ? new Date(o.created_at).toLocaleString() : ""}</span>
                    <span className="text-rose-600 font-semibold">{formatPrice(o.total_amount)}</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
          {totalPages > 1 ? (
            <div className="flex justify-center gap-3 pt-2">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="px-4 py-2 rounded-full border text-sm disabled:opacity-40"
              >
                上一页
              </button>
              <span className="text-sm text-gray-500 self-center">
                {page} / {totalPages}
              </span>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="px-4 py-2 rounded-full border text-sm disabled:opacity-40"
              >
                下一页
              </button>
            </div>
          ) : null}
        </div>
      </main>
      <Footer />
    </div>
  );
}
